import { useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import TableFilter from "src/components/TableFilter";
import DateUtils from "src/services/utils/DateUtils";
import { KYBMonitoringTypes } from "src/modules/3rd-party-management/constants";

const FilteringComponent = ({
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        created_from: null,
        created_to: null,
        supplier_name: null,
        type: null,
        false_positive: null,
        risk_rating: null,
        search_query: null
    });

    const initRef = useRef(false);

    const dateUtils = new DateUtils();

    const tableFilterTabs = useMemo(() => {
        const types = Object.keys(KYBMonitoringTypes).map((key) => {
            return {
                id: key,
                name: KYBMonitoringTypes[key].title
            }
        });

        let filtersList = [
            {
                type: 'date',
                name: 'filter-date',
                title: 'Creation Date',
                icon: <i className="ri-calendar-event-line" />,
                defaultValue: {
                    from: tableFilters.created_from,
                    to: tableFilters.created_to
                }
            },
            {
                type: 'radio_list',
                name: 'type',
                title: 'Type',
                icon: <i className="ri-filter-3-line" />,
                isLoading: false,
                defaultValue: tableFilters.type,
                data: types,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.name
                }
            },
            {
                type: 'radio_list',
                name: 'false_positive',
                title: 'False Positive',
                icon: <i className="ri-check-double-line" />,
                isLoading: false,
                defaultValue: tableFilters?.false_positive,
                data: [
                    {
                        id: true,
                        name: 'Yes'
                    },
                    {
                        id: false,
                        name: 'No'
                    }
                ],
                searchInListOption: {
                    enabled: false,
                    remote: false
                },
                formatter: (item) => {
                    return item.name
                }
            },
            {
                type: 'radio_list',
                name: 'risk_rating',
                title: 'Risk Rating',
                icon: <i className="ri-bar-chart-line" />,
                isLoading: false,
                defaultValue: tableFilters?.risk_rating,
                data: [
                    {
                        id: 'low',
                        name: 'Low'
                    },
                    {
                        id: 'medium',
                        name: 'Medium'
                    },
                    {
                        id: 'high',
                        name: 'High'
                    }
                ],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.name
                }
            },
            {
                type: 'search_textbox',
                name: 'search_query',
                title: 'Search Term',
                icon: <i className="ri-search-2-line" />,
                defaultValue: tableFilters?.search_query,
                options: {
                    placeholder: '',
                    typingDelay: 750
                },
                onFilterChange: (value) => {
                    if (value !== tableFilters.search_query) {
                        setTableFilters((currentState) => ({ ...currentState, search_query: value }))
                    }
                }
            },
            {
                type: 'search_textbox',
                name: 'supplier_name',
                title: 'Search in third parties',
                icon: <i className="ri-search-2-line" />,
                defaultValue: tableFilters?.supplier_name,
                options: {
                    placeholder: 'Search in third parties',
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    if (value !== tableFilters.supplier_name) {
                        setTableFilters((currentState) => ({ ...currentState, supplier_name: value }))
                    }
                }
            }
        ];

        return filtersList
    }, [
        tableFilters
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}

            if ('filter-date' in filters) {
                result['created_from'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.from),
                    'YYYY-MM-DD 00:00:00'
                )

                result['created_to'] = dateUtils.convertDateToDate(
                    new Date(filters['filter-date']?.to),
                    'YYYY-MM-DD 23:59:59'
                )
            }

            if ('type' in filters) {
                result.type = filters.type !== 'all' ? filters.type : undefined
            }

            if ('false_positive' in filters) {
                result.false_positive = filters.false_positive !== 'all' ? filters.false_positive : undefined
            }

            if ('risk_rating' in filters) {
                result.risk_rating = filters.risk_rating !== 'all' ? filters.risk_rating : undefined
            }

            if ('search_query' in filters) {
                result.search_query = filters.search_query || undefined;
            }

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                created_from: null,
                created_to: null,
                type: null,
                false_positive: null,
                risk_rating: null,
                search_query: null,
                supplier_name: independentTabsData?.supplier_name
            })
        }
    }

    useEffect(() => {
        if (!initRef.current) {
            initRef.current = true;
            return;
        }

        onChange && onChange(tableFilters)
    }, [
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='filter-date'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);
